import React,{useState,useEffect,useCallback,useRef} from 'react';
import {flushSync} from 'react-dom';
import {useLocation,useNavigate} from 'react-router-dom';
import {motion,AnimatePresence,useReducedMotion} from 'framer-motion';
import {DragDropContext} from 'react-beautiful-dnd';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Header from './Header';
import Sidebar from './Sidebar';
import DesktopNavigation,{DesktopTopbar,PRIMARY_WIDTH,FOLDER_WIDTH} from './DesktopChrome';
import AddBookmarkModal from '../Modals/AddBookmarkModal';
import {useTheme} from '../../contexts/ThemeContext';
import {useSettings} from '../../contexts/SettingsContext';
import {useBookmarks} from '../../contexts/BookmarkContext';
import {useAuth} from '../../contexts/AuthContext';
import {searchBookmarks,searchFolders} from '../../utils/search';
import {recordBookmarkOpen} from '../../utils/usage.js';

const {FiSearch,FiGlobe,FiFolder,FiX,FiArrowRight,FiPlus,FiEdit2,FiMenu}=FiIcons;
const APP_HEADER_OFFSET='calc(4rem + env(safe-area-inset-top, 0px))';
const SMOOTH_EASE=[0.16,1,0.3,1];
const isTabletTouchViewport=()=> typeof window!=='undefined' && window.innerWidth>=768 && window.innerWidth<1024 && window.innerHeight>=600;

const getHttpClipboardUrl=(value)=> {
  const trimmed=String(value || '').trim();
  if(!trimmed) return '';
  try {const parsed=new URL(trimmed);return ['http:','https:'].includes(parsed.protocol) ? trimmed : '';} catch {return '';}
};

const Layout=({children})=> {
  const location=useLocation();
  const navigate=useNavigate();
  const reduceMotion=useReducedMotion();
  const [mobileMenuOpen,setMobileMenuOpen]=useState(false);
  const [showPalette,setShowPalette]=useState(false);
  const [paletteQuery,setPaletteQuery]=useState('');
  const [paletteIndex,setPaletteIndex]=useState(0);
  const [clipboardPrefillUrl,setClipboardPrefillUrl]=useState('');
  const [isDesktop,setIsDesktop]=useState(()=> typeof window!=='undefined' ? window.innerWidth>=1024 : true);
  const [isTabletLayout,setIsTabletLayout]=useState(isTabletTouchViewport);
  const paletteInputRef=useRef(null);
  const clipboardRequestIdRef=useRef(0);
  const shortcutHandledRef=useRef(false);
  const {theme}=useTheme();
  const {user}=useAuth();
  const {settings,updateSetting}=useSettings();
  const {
    updateFolder,folders,bookmarks,moveBookmark,setCurrentFolder,editingBookmark,setEditingBookmark,isAddModalOpen,setIsAddModalOpen
  }=useBookmarks();

  const focusPaletteInput=useCallback(()=> {
    const input=paletteInputRef.current;
    if(!input) return false;
    try {input.focus({preventScroll:true});} catch {input.focus();}
    const caret=String(input.value || '').length;
    try {input.setSelectionRange(caret,caret);} catch {}
    return document.activeElement===input;
  },[]);

  useEffect(()=> {
    const handleResize=()=> {setIsDesktop(window.innerWidth>=1024);setIsTabletLayout(isTabletTouchViewport());};
    window.addEventListener('resize',handleResize);
    return ()=> window.removeEventListener('resize',handleResize);
  },[]);

  useEffect(()=> {
    if(mobileMenuOpen && !isDesktop && !isTabletLayout) {
      const originalOverflow=document.body.style.overflow;
      const originalOverscroll=document.body.style.overscrollBehavior;
      document.body.style.overflow='hidden';
      document.body.style.overscrollBehavior='none';
      return ()=> {document.body.style.overflow=originalOverflow;document.body.style.overscrollBehavior=originalOverscroll;};
    }
  },[mobileMenuOpen,isDesktop,isTabletLayout]);

  useEffect(()=> {
    if(!showPalette) return undefined;
    const originalOverflow=document.body.style.overflow;
    const originalOverscroll=document.body.style.overscrollBehavior;
    document.body.style.overflow='hidden';
    document.body.style.overscrollBehavior='none';
    let retry;
    const raf=requestAnimationFrame(()=> {
      focusPaletteInput();
      retry=window.setTimeout(focusPaletteInput,80);
    });
    return ()=> {cancelAnimationFrame(raf);if(retry) window.clearTimeout(retry);document.body.style.overflow=originalOverflow;document.body.style.overscrollBehavior=originalOverscroll;};
  },[showPalette,focusPaletteInput]);

  const closePalette=useCallback(()=> {setShowPalette(false);setPaletteQuery('');setPaletteIndex(0);},[]);
  const openPalette=useCallback(()=> {
    flushSync(()=> {setPaletteQuery('');setPaletteIndex(0);setShowPalette(true);});
    focusPaletteInput();
  },[focusPaletteInput]);
  const openEmptyBookmarkModal=useCallback(()=> {clipboardRequestIdRef.current+=1;setClipboardPrefillUrl('');setEditingBookmark(null);setIsAddModalOpen(true);},[setEditingBookmark,setIsAddModalOpen]);
  const closeBookmarkModal=useCallback(()=> {clipboardRequestIdRef.current+=1;setClipboardPrefillUrl('');setIsAddModalOpen(false);setEditingBookmark(null);},[setIsAddModalOpen,setEditingBookmark]);

  useEffect(()=> {
    if(shortcutHandledRef.current || typeof window==='undefined') return;
    const params=new URLSearchParams(window.location.search);
    const action=params.get('action');
    const view=params.get('view');
    if(!action && !view) return;
    shortcutHandledRef.current=true;

    if(action==='add') openEmptyBookmarkModal();
    else if(action==='search') openPalette();
    else if(view==='frequent') {setCurrentFolder('FREQUENT');if(location.pathname!=='/') navigate('/');}
    else if(view==='recent') {setCurrentFolder('RECENT');if(location.pathname!=='/') navigate('/');}

    params.delete('action');
    params.delete('view');
    const query=params.toString();
    const cleanUrl=`${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history.replaceState(window.history.state,'',cleanUrl);
  },[openEmptyBookmarkModal,openPalette,setCurrentFolder,navigate,location.pathname]);

  useEffect(()=> {
    const handleKeyDown=(event)=> {
      if(event.repeat) return;
      const isMac=navigator.platform.toUpperCase().includes('MAC');
      const modifier=isMac ? event.metaKey : event.ctrlKey;
      if(modifier && String(event.key).toLowerCase()==='k') {
        event.preventDefault();event.stopPropagation();
        if(showPalette) {flushSync(()=> {setPaletteQuery('');setPaletteIndex(0);});focusPaletteInput();}
        else openPalette();
      } else if(modifier && String(event.key).toLowerCase()==='n') {
        event.preventDefault();event.stopPropagation();
        const requestId=++clipboardRequestIdRef.current;
        setClipboardPrefillUrl('');setEditingBookmark(null);setIsAddModalOpen(true);
        if(window.isSecureContext && navigator.clipboard?.readText) {
          navigator.clipboard.readText().then(text=> {
            if(clipboardRequestIdRef.current!==requestId) return;
            const url=getHttpClipboardUrl(text);if(url) setClipboardPrefillUrl(url);
          }).catch(()=> {});
        }
      } else if(event.key==='Escape' && showPalette) {event.preventDefault();event.stopPropagation();closePalette();}
    };
    window.addEventListener('keydown',handleKeyDown,true);
    return ()=> window.removeEventListener('keydown',handleKeyDown,true);
  },[showPalette,closePalette,openPalette,focusPaletteInput,setIsAddModalOpen,setEditingBookmark]);

  useEffect(()=> setPaletteIndex(0),[paletteQuery]);

  const onDragEnd=async (result)=> {
    const {destination,source,draggableId,type}=result;
    if(!destination) return;
    if(destination.droppableId===source.droppableId && destination.index===source.index) return;

    if(type==='folder') {
      const newParentId=destination.droppableId==='root-folders' ? null : destination.droppableId;
      try {await updateFolder(draggableId,{parent:newParentId,sortOrder:destination.index});} catch(error) {console.error('Failed to move folder:',error);}
      return;
    }
    if(type!=='bookmark') return;

    let targetFolderId;
    let destinationIndex=destination.index;
    if(destination.droppableId.startsWith('folder-drop-')) {
      targetFolderId=destination.droppableId.replace('folder-drop-','');
      destinationIndex=(bookmarks || []).filter(bookmark=> bookmark.folderId===targetFolderId).length;
    } else if(destination.droppableId.startsWith('bookmarks-')) {
      const suffix=destination.droppableId.replace('bookmarks-','');
      if(['ALL','FREQUENT','RECENT','HEALTH'].includes(suffix) || suffix.startsWith('SMART:')) return;
      targetFolderId=suffix==='UNFILED' ? null : suffix;
    } else return;

    try {
      await moveBookmark(draggableId,targetFolderId,destinationIndex);
      if(settings.sortBy!=='custom') await updateSetting('sortBy','custom');
    } catch(error) {console.error('Failed to move bookmark:',error);}
  };

  const trimmedPaletteQuery=paletteQuery.trim();
  const commandCandidates=[
    {id:'all',name:'Show All Bookmarks',type:'command',icon:FiGlobe},
    {id:'frequent',name:'Show Frequently Used',type:'command',icon:FiIcons.FiZap},
    {id:'recent',name:'Show Recent',type:'command',icon:FiIcons.FiClock},
    {id:'unfiled',name:'Show Unfiled',type:'command',icon:FiFolder},
    {id:'settings',name:'App Settings',type:'command',icon:FiIcons.FiSettings}
  ];
  const filteredPaletteItems=trimmedPaletteQuery ? [
    ...searchBookmarks(bookmarks || [],trimmedPaletteQuery,folders || []).slice(0,5).map(bookmark=> ({...bookmark,type:'bookmark'})),
    ...searchFolders(folders || [],trimmedPaletteQuery).slice(0,3).map(folder=> ({...folder,type:'folder'})),
    ...commandCandidates.filter(command=> command.name.toLowerCase().includes(trimmedPaletteQuery.toLowerCase()))
  ] : [];

  const handlePaletteSelect=(item)=> {
    if(item.type==='bookmark') {if(user?.id) void recordBookmarkOpen(user.id,item.id);window.open(item.url,'_blank','noopener,noreferrer');}
    else if(item.type==='folder') {setCurrentFolder(item.id);if(location.pathname!=='/') navigate('/');}
    else if(item.id==='all') {setCurrentFolder('ALL');navigate('/');}
    else if(item.id==='frequent') {setCurrentFolder('FREQUENT');navigate('/');}
    else if(item.id==='recent') {setCurrentFolder('RECENT');navigate('/');}
    else if(item.id==='unfiled') {setCurrentFolder('UNFILED');navigate('/');}
    else if(item.id==='settings') navigate('/settings');
    closePalette();
  };

  const handlePaletteEdit=(item,event)=> {event.preventDefault();event.stopPropagation();if(item.type!=='bookmark') return;setEditingBookmark(item);closePalette();};
  const handlePaletteInputKeyDown=(event)=> {
    if(event.key==='ArrowDown') {event.preventDefault();if(filteredPaletteItems.length) setPaletteIndex(current=> (current+1)%filteredPaletteItems.length);}
    else if(event.key==='ArrowUp') {event.preventDefault();if(filteredPaletteItems.length) setPaletteIndex(current=> (current-1+filteredPaletteItems.length)%filteredPaletteItems.length);}
    else if(event.key==='Enter') {const selected=filteredPaletteItems[paletteIndex];if(selected){event.preventDefault();handlePaletteSelect(selected);}}
  };

  const desktopLeft=location.pathname==='/' ? PRIMARY_WIDTH+FOLDER_WIDTH : PRIMARY_WIDTH;
  const drawerTransition=reduceMotion ? {duration:0.01} : {duration:0.34,ease:SMOOTH_EASE};
  const overlayTransition=reduceMotion ? {duration:0.01} : {duration:0.22,ease:'easeOut'};
  const paletteTransition=reduceMotion ? {duration:0.01} : {duration:0.28,ease:SMOOTH_EASE};

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className={`min-h-[100dvh] transition-colors duration-200 ${theme==='dark' ? 'bg-zinc-950 text-white' : 'bg-[#f7f9fc] text-slate-900'}`}>
        <div className="lg:hidden"><Header onMobileMenuClick={()=> setMobileMenuOpen(!mobileMenuOpen)} onTabletToggle={()=> {}} mobileMenuOpen={mobileMenuOpen} tabletSidebarCollapsed={false} /></div>
        <DesktopNavigation />
        <DesktopTopbar />

        <AnimatePresence initial={false}>
          {mobileMenuOpen && !isDesktop && <>
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={overlayTransition} onClick={()=> setMobileMenuOpen(false)} className="lg:hidden fixed inset-0 z-40" style={{top:APP_HEADER_OFFSET,backgroundColor:isTabletLayout ? 'transparent' : 'rgba(15,23,42,0.48)',pointerEvents:isTabletLayout ? 'none' : 'auto'}} />
            <motion.div initial={{x:reduceMotion ? 0 : '-100%'}} animate={{x:0}} exit={{x:reduceMotion ? 0 : '-100%'}} transition={drawerTransition} role="dialog" aria-modal={!isTabletLayout} aria-label="Library browser" className={`lg:hidden fixed left-0 w-full z-50 shadow-2xl overflow-hidden border-r isolate transform-gpu ${theme==='dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`} style={{top:APP_HEADER_OFFSET,bottom:0,width:isTabletLayout ? 'min(24rem, 50vw)' : '100%',paddingBottom:'env(safe-area-inset-bottom, 0px)',willChange:'transform'}}>
              <Sidebar isCollapsed={false} onClose={()=> setMobileMenuOpen(false)} isMobile={true} />
            </motion.div>
          </>}
        </AnimatePresence>

        <main className="min-w-0 transition-[margin-left] duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]" style={{marginLeft:isDesktop ? `${desktopLeft}px` : (mobileMenuOpen && isTabletLayout ? '24rem' : '0'),paddingTop:isDesktop ? '4rem' : '0'}}>
          <div className="p-3 sm:p-4 lg:p-5 xl:p-6 max-w-full pb-36 lg:pb-10">{children}</div>
        </main>

        {!mobileMenuOpen && <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[45] bg-white/[0.98] dark:bg-slate-900/[0.98] border-t border-gray-200/80 dark:border-slate-800 pb-[calc(env(safe-area-inset-bottom,0px)+0.625rem)] pt-1 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)]" aria-label="Primary navigation"><div className="flex items-center justify-around h-16 px-2"><button onClick={()=> setMobileMenuOpen(true)} className="flex flex-col items-center justify-center space-y-1 w-full h-full min-h-11 rounded-xl text-zinc-500 active:text-indigo-600 active:bg-indigo-500/5 transition-colors" aria-label="Open library"><SafeIcon icon={FiMenu} className="w-6 h-6" /><span className="text-[10px] font-black uppercase tracking-widest">Library</span></button><button onClick={openPalette} className="flex flex-col items-center justify-center space-y-1 w-full h-full min-h-11 rounded-xl text-zinc-500 active:text-indigo-600 active:bg-indigo-500/5 transition-colors" aria-label="Search bookmarks"><SafeIcon icon={FiSearch} className="w-6 h-6" /><span className="text-[10px] font-black uppercase tracking-widest">Search</span></button><button onClick={openEmptyBookmarkModal} className="flex flex-col items-center justify-center space-y-1 w-full h-full min-h-11 rounded-xl text-zinc-500 active:text-indigo-600 active:bg-indigo-500/5 transition-colors" aria-label="Add bookmark"><SafeIcon icon={FiPlus} className="w-6 h-6" /><span className="text-[10px] font-black uppercase tracking-widest">Add</span></button></div></nav>}

        <AddBookmarkModal isOpen={isAddModalOpen || !!editingBookmark} onClose={closeBookmarkModal} editBookmark={editingBookmark} initialUrl={editingBookmark ? '' : clipboardPrefillUrl} />

        <AnimatePresence initial={false}>
          {showPalette && <div className="fixed inset-0 z-[100] isolate flex items-start justify-center pt-[max(calc(env(safe-area-inset-top,0px)+0.75rem),1rem)] sm:pt-[max(10vh,env(safe-area-inset-top,20px))] px-3 sm:px-4 pb-[max(1rem,env(safe-area-inset-bottom,20px))]">
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={overlayTransition} onClick={closePalette} className="absolute inset-0 z-0 bg-slate-950/75" />
            <motion.div initial={{opacity:0,y:reduceMotion ? 0 : -18}} animate={{opacity:1,y:0}} exit={{opacity:0,y:reduceMotion ? 0 : -12}} transition={paletteTransition} style={{willChange:'transform, opacity'}} className={`relative z-10 w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden border flex flex-col max-h-[82dvh] transform-gpu ${theme==='dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center p-3 sm:p-4 border-b dark:border-slate-700"><SafeIcon icon={FiSearch} className="w-5 h-5 text-gray-400 mr-3" /><input ref={paletteInputRef} autoFocus type="search" value={paletteQuery} onChange={event=> setPaletteQuery(event.target.value)} onKeyDown={handlePaletteInputKeyDown} placeholder="Search bookmarks, folders, or commands..." className="w-full bg-transparent border-none focus:ring-0 text-base sm:text-lg outline-none" inputMode="search" enterKeyHint="search" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} aria-label="Search bookmarks, folders, or commands" /><button onClick={closePalette} className="p-2.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors" aria-label="Close search"><SafeIcon icon={FiX} className="w-4 h-4 text-gray-400" /></button></div>
              <div className="flex-1 overflow-y-auto overscroll-contain p-2">{filteredPaletteItems.length ? filteredPaletteItems.map((item,index)=> <div key={`${item.type}-${item.id}`} role="button" tabIndex={0} onClick={()=> handlePaletteSelect(item)} onKeyDown={event=> {if(event.key==='Enter') handlePaletteSelect(item);}} onMouseEnter={()=> setPaletteIndex(index)} className={`flex items-center w-full p-3 rounded-xl text-left transition-colors cursor-pointer ${paletteIndex===index ? (theme==='dark' ? 'bg-slate-700' : 'bg-indigo-50') : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}><div className={`p-2 rounded-lg mr-3 flex-shrink-0 ${item.type==='bookmark' ? 'bg-blue-500/10 text-blue-500' : item.type==='folder' ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}><SafeIcon icon={item.icon || (item.type==='bookmark' ? FiGlobe : FiFolder)} className="w-4 h-4" /></div><div className="flex-1 min-w-0"><p className={`text-sm font-bold truncate ${paletteIndex===index && theme!=='dark' ? 'text-indigo-700' : ''}`}>{item.title || item.name}</p>{item.url && <p className="text-[10px] text-gray-500 truncate font-medium mt-0.5">{item.url}</p>}</div>{item.type==='bookmark' && <button type="button" onClick={event=> handlePaletteEdit(item,event)} className={`ml-2 p-2.5 rounded-lg transition-colors flex-shrink-0 ${theme==='dark' ? 'hover:bg-slate-600' : 'hover:bg-white'}`} title="Edit"><SafeIcon icon={FiEdit2} className="w-4 h-4 text-gray-400 hover:text-indigo-500" /></button>}<SafeIcon icon={FiArrowRight} className={`w-4 h-4 text-indigo-500 ml-2 transition-opacity flex-shrink-0 ${paletteIndex===index ? 'opacity-100' : 'opacity-0'}`} /></div>) : <div className="p-10 sm:p-12 text-center text-gray-500"><div className="w-16 h-16 bg-gray-50 dark:bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4"><SafeIcon icon={FiSearch} className="w-8 h-8 opacity-20" /></div><p className="text-sm font-bold">{paletteQuery ? 'No results found' : 'Start typing to search...'}</p></div>}</div>
            </motion.div>
          </div>}
        </AnimatePresence>
      </div>
    </DragDropContext>
  );
};

export default Layout;